/**
 * A discriminated union type representing either a successful result (Ok)
 * containing data of type T, or an error result (Err) containing an error of type E.
 *
 * This utility type is introduced to provide a better alternative to the default error handling paradigm with throw and try/catch,
 * allowing functions to return results that explicitly indicate success or failure without using exceptions.
 * Errors are treated as regular values, which helps to improve code readability and maintainability due to the more straightforward control flow.
 * Errors are explicit and must be handled (or at least thought about) by the caller, reducing the likelihood of unhandled exceptions.
 *
 * @template T - The type of the successful result data.
 * @template E - The type of the error (default is unknown).
 */
export type Result<T = void, E = DefaultErrorData> = Ok<T> | Err<E>

export interface DefaultErrorData {
  message: string
}

export type ErrFromExceptionData = DefaultErrorData & {
  originalError?: Error
}

export interface Err<E> {
  ok: false
  error: E
  stackTrace: string
}

export interface Ok<T> {
  ok: true
  // NOTE: allows for data to be optional if T is void or undefined
  data: T extends undefined ? T | undefined : T
}

// Type Guards
// -----------------------------------------------------------------------------

/**
 * Type guard to check if a Result is an Ok variant.
 */
export function isOk<T, E>(response: Result<T, E>): response is Ok<T> {
  return response.ok === true
}

/**
 * Type guard to check if a Result is an Err variant.
 */
export function isErr<T, E>(response: Result<T, E>): response is Err<E> {
  return response.ok === false
}

/**
 * Asserts that a Result is an Ok variant.
 * Throws an error if the Result is an Err variant.
 *
 * Should only be used in test code, where you know a Result will be Ok.
 *
 * @throws {Error} If the Result is an Err variant.
 */
export function assertOk<T, E>(response: Result<T, E>): asserts response is Ok<T> {
  if (!isOk(response)) {
    throw new Error(`Expected Ok result, but got Err: ${response.error}`)
  }
}

/**
 * Asserts that a Result is an Err variant.
 * Throws an error if the Result is an Ok variant.
 *
 * Should only be used in test code, where you know a Result will be Err.
 *
 * @throws {Error} If the Result is an Ok variant.
 */
export function assertErr<T, E>(response: Result<T, E>): asserts response is Err<E> {
  if (!isErr(response)) {
    throw new Error(`Expected Err result, but got Ok: ${response.data}`)
  }
}

// Factory Functions
// -----------------------------------------------------------------------------

// NOTE: function overloads to make data parameter optional if T is void or undefined
/**
 * Creates an Ok Result containing the provided data.
 * If T is void or undefined, the data parameter is optional.
 */
export function ok(): Ok<void>
export function ok<T>(data: T): Ok<T>
export function ok<T>(data?: T): Ok<T>
export function ok<T>(data?: T): Ok<T> {
  return {
    ok: true,
    data: data as T extends undefined ? T | undefined : T,
  }
}

/**
 * Creates an Err Result containing the provided error.
 */
export function err<E>(error: E): Err<E> {
  const stackTraceWrapper = {
    stack: '',
  }
  Error.captureStackTrace(stackTraceWrapper, err)

  return {
    ok: false,
    error,
    stackTrace: stackTraceWrapper.stack,
  }
}

/**
 * Creates an `Err<DefaultErrorData>` Result from an exception (parameter of catch block).
 *
 * ```ts
 * try {
 *   // some code that may throw
 * } catch (exception) {
 *   return errFromException(exception)
 * }
 * ```
 *
 */
export function errFromException(exception: Error | unknown): Err<ErrFromExceptionData> {
  if (exception instanceof Error) {
    return err({
      message: exception.message,
      originalError: exception,
    })
  }

  return err({ message: String(exception) })
}

// Utility Functions
// -----------------------------------------------------------------------------
export type LogErrOptions = {
  debugMessage?: string
}
export function logErr(errorResult: Err<unknown>, options?: LogErrOptions): void {
  console.error(logErrMessage(errorResult, options))
}

export function logErrWarn(errorResult: Err<unknown>, options?: LogErrOptions): void {
  console.warn(logErrMessage(errorResult, options))
}

function logErrMessage(errorResult: Err<unknown>, options?: LogErrOptions): string {
  const message = []

  if (options?.debugMessage) {
    message.push(`Debug Message: ${options.debugMessage}`)
  }

  message.push(`Error:\n${JSON.stringify(errorResult.error)}`)
  message.push(`Stack Trace: ${errorResult.stackTrace}`)

  return message.join('\n')
}
